import { OrdersAnalyticsFilters } from "@/features/orders-analytics/types/orders-analytics.types";
import { httpClient } from "@/shared/http/httpClient";

export type MetaInsightLevel = "campaign" | "adset" | "ad";

export type CampaignInsightsRowDto = {
    objectId: string;
    objectName: string;
    level: MetaInsightLevel;
    spend: number;
    impressions: number;
    clicks: number;
    // null when conversions were not derivable for this row — render an empty-state,
    // never a literal 0 for a campaign that may be converting fine.
    results: number | null;
    ctr: number | null;
    cpc: number | null;
    cpm: number | null;
    costPerResult: number | null;
    currency: string;
};

export type CampaignInsightsReadModelDto = {
    totals: { spend: number; impressions: number; clicks: number; results: number | null };
    // One row per campaign for the whole window (snapshots are day-grained and are
    // collapsed server-side), never one row per day.
    rows: CampaignInsightsRowDto[];
    // True when the window matched a snapshot covering more than one day, whose spend
    // may double-count. Optional on the wire for the same version-skew reason as
    // `degraded`.
    containsLegacyGrain?: boolean;
};

export type CohortValueDto = {
    audienceSpecId: string;
    label: string;
    memberCount: number;
    value: number;
    truncated: boolean;
};

export type CohortValueReadModelDto = {
    totals: { value: number };
    rows: CohortValueDto[];
    truncated: boolean;
    // audienceSpecIds whose read failed: they are missing from `rows` AND from
    // `totals.value`, so the total is an undercount. Optional on the wire so a
    // frontend deployed ahead of the backend degrades to "no signal" rather than
    // crashing on a field the older payload does not carry.
    degraded?: string[];
};

export type AttributionDto = {
    // null = not derivable (spend section degraded / several currencies in the window;
    // value section degraded). A number is always a measured figure.
    totalSpend: number | null;
    spendCurrency: string | null;
    totalValue: number | null;
    valueCurrency: string;
    blendedRoas: number | null;
};

export type RecommendationDto = {
    code: string;
    severity: "info" | "warning";
    message: string;
};

export type MetaGrowthDashboardDto = {
    window: { from: number; to: number };
    attribution: AttributionDto;
    campaigns: CampaignInsightsReadModelDto | null;
    cohorts: CohortValueReadModelDto | null;
    recommendations: RecommendationDto[];
};

const API_PATH = "/api/v1/analytics/meta/growth";

// `filters.clients` is deliberately NOT forwarded: the endpoint forces its tenant
// server-side (a caller can never read another tenant's data), so sending a client
// would be a request the backend ignores. The page hides the selector to match — see
// MetaGrowthPage's showClientSelector={false}.
const fetchDashboard = (filters: OrdersAnalyticsFilters): Promise<MetaGrowthDashboardDto> => {
    const params = new URLSearchParams();
    params.set("from", String(filters.dateFrom));
    params.set("to", String(filters.dateTo));

    return httpClient.getJson<MetaGrowthDashboardDto>(`${API_PATH}?${params.toString()}`);
};

export const metaGrowthApi = { fetchDashboard };
